/* eslint-disable no-unused-vars */
import React from 'react';
import { motion } from 'framer-motion';

export default function AboutSection() {
  return (
    <section className="relative bg-[#FFF9F5] overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-orange-200/40 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-orange-100 rounded-full blur-[120px] translate-x-1/4 translate-y-1/4" />

      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 relative z-10">
        
        {/* --- SECTION 1: ABOUT --- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 md:mb-32">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="order-2 lg:order-1"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-600 font-bold text-xs tracking-widest uppercase mb-6">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
              Who We Are
            </div>
            
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 leading-tight mb-6 uppercase">
              About <span className="text-orange-600">Mahakal</span> <br />
              Social Welfare
            </h2>
            
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              We are a non-profit organization dedicated to improving lives and 
              promoting social justice. By addressing the root causes of inequality, 
              we build a more equitable society through job training, financial literacy, 
              and dedicated counseling for those in need.
            </p>

            <a
              href="/Contact"
              className="inline-flex items-center gap-3 bg-gray-900 text-white px-8 py-4 rounded-2xl font-bold hover:bg-orange-600 transition-all shadow-xl hover:shadow-orange-200 group"
            >
              Contact Us Now
              <svg className="w-5 h-5 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="order-1 lg:order-2 relative"
          >
            {/* Orange Blur Card Backdrop */}
            <div className="absolute inset-0 bg-orange-500/10 blur-3xl rounded-[3rem]" />
            <div className="relative p-3 bg-white/40 backdrop-blur-md border border-white/50 rounded-[2.5rem] shadow-2xl">
              <img
                className="w-full h-[300px] md:h-[450px] object-cover rounded-[2rem]"
                src="https://ik.imagekit.io/hickter/Hickter_Projects/MSWS/OTHERS/socialabout.png?updatedAt=1708347173488"
                alt="About Mahakal"
              />
            </div>
          </motion.div>
        </div>

        {/* --- SECTION 2: MISSION (Full Width Orange Style) --- */}
        <div className="relative group">
          {/* Glassmorphic Background Card */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600 to-orange-800 rounded-[3rem] shadow-2xl overflow-hidden">
             {/* Animated subtle blur spots inside the card */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20 group-hover:bg-white/20 transition-colors" />
          </div>

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch min-h-[500px]">
            <div className="p-8 md:p-16 flex flex-col justify-center order-2 lg:order-1">
              <h3 className="text-white text-3xl md:text-5xl font-black mb-8 uppercase tracking-tight">
                Our <span className="text-orange-300">Mission</span>
              </h3>
              
              <div className="space-y-6">
                {[
                  { title: "Addressing Issues", desc: "Tackling poverty, hunger, and lack of education at the source." },
                  { title: "Promoting Equity", desc: "Creating a just society where everyone has the opportunity to thrive." },
                  { title: "Empowering Communities", desc: "Training individuals to become self-reliant and advocate for their rights." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 hover:bg-white/20 transition-all">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-400 text-orange-950 flex items-center justify-center font-black text-sm">
                      {i + 1}
                    </span>
                    <div>
                      <h4 className="text-white font-bold text-lg">{item.title}</h4>
                      <p className="text-orange-100/80 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative order-1 lg:order-2 overflow-hidden min-h-[300px] lg:min-h-full">
              <img
                className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60 lg:opacity-100 lg:mix-blend-normal"
                src="https://ik.imagekit.io/hickter/Hickter_Projects/MSWS/OTHERS/missionmsws.png?updatedAt=1708347688799"
                alt="Our Mission"
              />
              {/* Image Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-800 lg:from-transparent to-transparent" />
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}